import React from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import client from './query-client';
import { QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { startMSW } from './mocks/worker';

import './index.css';

import { AuthProfileProvider } from './providers/AuthProfileProvider';
import { FirmProvider } from './providers/FirmProvider';
import { UserFirmRoleProvider } from './providers/UserFirmRoleProvider';
import DocumentViewer from './pages/DocumentViewer';
import XeroCallback from './XeroCallback';
import InvitationsPage from './pages/InvitationsPage';
import * as Sentry from '@sentry/react';
import ErrorBoundary from './ErrorBoundary';
import { Slide, ToastContainer } from 'react-toastify';
import { store } from './store';
import { Provider } from 'react-redux';
import FirmValidator from './FirmValidator';
import OnboardingPage from './pages/OnboardingPage';
import OnboardGuard from './guards/OnboardGuard';
import AutoAcceptInvitationPage from './pages/AutoAcceptInvitationPage';
import AuthWrapper from './providers/AuthWrapper';
import IntercomProvider from './providers/IntercomProvider';
import MaintenancePage from './pages/MaintenancePage';

/**
 * Enable mocking of api requests.
 * This is useful for both testing and to deploy a "demo" application.
 *
 * DO NOT enable this for a production build
 */
if (process.env.REACT_APP_MOCK_API === 'true') {
    startMSW();
}

if (process.env.REACT_APP_MOCK_API !== 'true') {
    Sentry.init({
        dsn: 'https://d7bafbfecfc34548bab98fca01beae77@o1355432.ingest.sentry.io/6639917',
        // integrations: [new BrowserTracing()],
        enabled: process.env.NODE_ENV === 'production',
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 0,

        beforeSend(event) {
            // Check if it is an exception, and if so, show the report dialog
            if (event.exception) {
                Sentry.showReportDialog({ eventId: event.event_id });
            }
            return event;
        },
    });
}

ReactDOM.render(
    <React.StrictMode>
        <Provider store={store}>
            <ToastContainer newestOnTop transition={Slide} position="top-right" hideProgressBar icon={false} />
            <ErrorBoundary>
                <BrowserRouter>
                    {/* LOGIN TO AUTH0 */}
                    <AuthWrapper>
                        <QueryClientProvider client={client}>
                            <AuthProfileProvider>
                                <Routes>
                                    <Route path="/maintenance" element={<MaintenancePage />} />
                                    <Route path="/accept-invitation" element={<AutoAcceptInvitationPage />} />
                                    <Route path="/onboarding" element={<OnboardingPage />} />
                                    <Route element={<OnboardGuard />}>
                                        <Route path="/callback/xero" element={<XeroCallback />} />
                                        {/* <Route path="/start/setup" element={<FirmSetupPage />} /> */}
                                        <Route path="/start/invitations" element={<InvitationsPage />} />
                                        {['/', '/:firmShortId/*'].map((path, i) => (
                                            <Route
                                                key={i}
                                                path={path}
                                                element={
                                                    <FirmValidator>
                                                        <FirmProvider>
                                                            <UserFirmRoleProvider>
                                                                <IntercomProvider>
                                                                    <Routes>
                                                                        <Route path="*" element={<App />} />
                                                                        <Route
                                                                            path="document-view/:recordId"
                                                                            element={<DocumentViewer />}
                                                                        />
                                                                    </Routes>
                                                                </IntercomProvider>
                                                            </UserFirmRoleProvider>
                                                        </FirmProvider>
                                                    </FirmValidator>
                                                }
                                            />
                                        ))}
                                    </Route>
                                </Routes>
                            </AuthProfileProvider>
                        </QueryClientProvider>
                    </AuthWrapper>
                </BrowserRouter>
            </ErrorBoundary>
        </Provider>
    </React.StrictMode>,
    document.getElementById('root')
);
